import React, { useState, useEffect } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Sparkles, Loader2, Save, Check, Send, ShieldAlert, ListChecks, TrendingUp, X, CalendarPlus,
  Mail, FileText, CalendarClock, ExternalLink, ShieldCheck,
} from "lucide-react";

const EMAIL_STATUS = {
  queued: { label: "queued", color: "#B7791F" },
  sending: { label: "sending", color: "#B7791F" },
  sent: { label: "sent", color: "#2F7D5A" },
  failed: { label: "failed", color: "#9C1E22" },
  skipped: { label: "queued only (provider not configured)", color: "#6B7280" },
};
const fmtDT = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

export default function MeetingSummary({ meetingId, summary, status, onChange }) {
  const [s, setS] = useState(summary || null);
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [apply, setApply] = useState({ add_checklist: true, create_tasks: false, set_next_meeting: false });
  const [emailOpen, setEmailOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastEmail, setLastEmail] = useState(null);

  const loadLastEmail = () => api.get(`/meetings/${meetingId}/summary/last-email`).then(({ data }) => setLastEmail(data.last_email)).catch(() => {});
  useEffect(() => { if (s?.status === "approved") loadLastEmail(); }, [s?.status, meetingId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setS(summary || null); }, [summary]);
  const loadSuggestions = () => api.get(`/meetings/${meetingId}/suggestions`).then(({ data }) => setSuggestions(data.suggestions || [])).catch(() => {});
  useEffect(() => { loadSuggestions(); }, [meetingId]); // eslint-disable-line

  const generate = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/meetings/${meetingId}/summary/generate`); setS(data.summary); toast.success(data.summary.fallback ? "Draft created (AI unavailable — used a safe template)." : "AI draft ready. Please review."); onChange && onChange(); }
    catch (e) { toast.error(errMsg(e, "Could not generate summary.")); } finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put(`/meetings/${meetingId}/summary`, s); setS(data.summary); toast.success("Saved."); onChange && onChange(); }
    catch (e) { toast.error(errMsg(e, "Save failed.")); } finally { setBusy(false); }
  };
  const approve = async (share) => {
    setBusy(true);
    try { const { data } = await api.post(`/meetings/${meetingId}/summary/approve`, { share_with_client: share }); setS(data.summary); toast.success("Approved."); onChange && onChange(); }
    catch (e) { toast.error(errMsg(e, "Approve failed.")); } finally { setBusy(false); }
  };
  const doApply = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/meetings/${meetingId}/summary/apply`, apply); toast.success(`Applied: ${data.applied.checklist_items} checklist item(s), ${data.applied.tasks} task(s).`); onChange && onChange(); }
    catch (e) { toast.error(errMsg(e, "Apply failed.")); } finally { setBusy(false); }
  };
  const resolve = async (sid, action) => {
    try { await api.post(`/suggestions/${sid}/${action}`); toast.success(action === "accept" ? "Pipeline updated." : "Suggestion declined."); loadSuggestions(); }
    catch (e) { toast.error(errMsg(e, "Failed.")); }
  };
  const openEmail = async () => {
    setEmailOpen(true); setPreview(null); setPreviewLoading(true);
    try {
      const { data } = await api.get(`/meetings/${meetingId}/summary/email-preview`, { params: { base_url: window.location.origin } });
      setPreview(data);
    } catch (e) { toast.error(errMsg(e, "Could not build the family email preview.")); setEmailOpen(false); }
    finally { setPreviewLoading(false); }
  };
  const sendEmail = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/meetings/${meetingId}/summary/email`, { base_url: window.location.origin });
      toast.success(`Sent to ${data.masked_email}. ${data.delivery_note}`);
      setEmailOpen(false); loadLastEmail(); onChange && onChange();
    } catch (e) { toast.error(errMsg(e, "Could not send the summary email.")); }
    finally { setSending(false); }
  };

  const setField = (k, v) => setS({ ...s, [k]: v });
  const setChecklistItem = (i, k, v) => { const cl = [...(s.checklist || [])]; cl[i] = { ...cl[i], [k]: v }; setS({ ...s, checklist: cl }); };
  const removeChecklistItem = (i) => setS({ ...s, checklist: (s.checklist || []).filter((_, j) => j !== i) });

  return (
    <Card className="p-4 border-[#E6EDF2]" data-testid="meeting-summary-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70"><Sparkles size={14} /> AI meeting summary</div>
        {!s && <Button size="sm" data-testid="summary-generate-btn" onClick={generate} disabled={busy} className="h-8 bg-[#7C3AED] hover:bg-[#6D28D9] text-white gap-1 text-[12px]">{busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Generate draft</Button>}
      </div>

      {/* Pipeline suggestions */}
      {suggestions.length > 0 && (
        <div className="mb-3 rounded-lg border border-[#B7791F]/30 bg-[#FBF6EC] p-3" data-testid="pipeline-suggestions">
          <div className="flex items-center gap-1.5 text-[12px] font-semibold text-[#B7791F] mb-1.5"><TrendingUp size={14} /> Suggested pipeline updates (you confirm)</div>
          {suggestions.map((sg) => (
            <div key={sg.id} className="flex items-center justify-between gap-2 text-[13px] py-1" data-testid={`suggestion-${sg.id}`}>
              <span className="text-[#252221]">Move to <b>{sg.stage_label}</b> — {sg.reason}</span>
              <span className="flex gap-1 shrink-0">
                <button onClick={() => resolve(sg.id, "accept")} data-testid={`suggestion-accept-${sg.id}`} className="h-7 px-2 rounded-md bg-[#2F7D5A] text-white text-[12px] inline-flex items-center gap-1"><Check size={12} /> Accept</button>
                <button onClick={() => resolve(sg.id, "decline")} data-testid={`suggestion-decline-${sg.id}`} className="h-7 px-2 rounded-md border border-[#D6E0E7] text-[#4B5563] text-[12px] inline-flex items-center gap-1"><X size={12} /> Decline</button>
              </span>
            </div>
          ))}
        </div>
      )}

      {!s ? <p className="text-[13px] text-[#9AA5B1]">Generate an AI draft summary after the meeting. It stays a draft until you review and approve.</p> : (
        <div className="space-y-3">
          <div className="flex items-start gap-1.5 rounded-lg bg-[#7C3AED]/8 text-[#6D28D9] px-3 py-2 text-[12px]" data-testid="summary-disclaimer">
            <ShieldAlert size={14} className="mt-0.5 shrink-0" /> AI summary is a draft — it may be incomplete or incorrect. Review before acting or sharing. No legal, medical, financial, tax, real-estate, or placement advice.
          </div>
          <div className="flex items-center gap-2 text-[12px]">
            <span className="px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: s.status === "approved" ? "#2F7D5A18" : "#B7791F18", color: s.status === "approved" ? "#2F7D5A" : "#B7791F" }} data-testid="summary-status">{s.status}</span>
            {s.fallback && <span className="text-[#9AA5B1]">template fallback</span>}
          </div>

          <div><Label className="text-[12px] text-[#4B5563]">Attendees</Label><Input value={s.attendees || ""} onChange={(e) => setField("attendees", e.target.value)} className="h-9 text-[14px]" data-testid="summary-attendees" /></div>
          <div><Label className="text-[12px] text-[#4B5563]">Situation discussed</Label><Textarea value={s.situation || ""} onChange={(e) => setField("situation", e.target.value)} rows={3} className="text-[14px]" data-testid="summary-situation" /></div>

          {(s.key_decisions || []).length > 0 && <div><Label className="text-[12px] text-[#4B5563]">Key decisions</Label><ul className="list-disc pl-5 text-[13px] text-[#252221]">{s.key_decisions.map((d, i) => <li key={i}>{d}</li>)}</ul></div>}

          <div className="grid grid-cols-2 gap-3 text-[13px]">
            <div><Label className="text-[12px] text-[#4B5563]">Suggested follow-up date</Label><Input value={s.suggested_follow_up_date || ""} onChange={(e) => setField("suggested_follow_up_date", e.target.value)} placeholder="YYYY-MM-DD" className="h-9 text-[14px]" /></div>
            <div><Label className="text-[12px] text-[#4B5563]">Suggested next meeting</Label><Input value={s.suggested_next_meeting_date || ""} onChange={(e) => setField("suggested_next_meeting_date", e.target.value)} placeholder="YYYY-MM-DD" className="h-9 text-[14px]" data-testid="summary-next-meeting" /></div>
          </div>
          {s.suggested_stage_label && <div className="text-[13px] text-[#4B5563]">Suggested CRM stage: <b className="text-[#1C4358]">{s.suggested_stage_label}</b></div>}

          {/* Auto-drafted checklist */}
          <div data-testid="summary-checklist">
            <div className="flex items-center gap-1.5 text-[12px] font-semibold text-[#1C4358] mb-1"><ListChecks size={14} /> Draft follow-up checklist</div>
            {(s.checklist || []).length === 0 ? <p className="text-[12px] text-[#9AA5B1]">No checklist items drafted.</p> : (
              <div className="space-y-1.5">
                {s.checklist.map((c, i) => (
                  <div key={i} className="rounded-lg border border-[#E6EDF2] p-2" data-testid={`summary-checklist-${i}`}>
                    <div className="flex items-center gap-2">
                      <Input value={c.title || ""} onChange={(e) => setChecklistItem(i, "title", e.target.value)} className="h-8 text-[13px]" />
                      <select value={c.visibility || "internal"} onChange={(e) => setChecklistItem(i, "visibility", e.target.value)} className="h-8 rounded border border-[#D6E0E7] text-[12px] px-1">
                        <option value="internal">Internal</option><option value="client">Client</option><option value="professional">Professional</option>
                      </select>
                      <button onClick={() => removeChecklistItem(i)} className="text-[#9C1E22]"><X size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={save} disabled={busy} className="border-[#D6E0E7] text-[#1C4358] gap-1" data-testid="summary-save-btn"><Save size={14} /> Save edits</Button>
            <Button size="sm" onClick={() => generate()} disabled={busy} variant="outline" className="border-[#7C3AED]/40 text-[#7C3AED] gap-1"><Sparkles size={14} /> Regenerate</Button>
            {s.status !== "approved" ? (
              <Button size="sm" onClick={() => approve(false)} disabled={busy} className="bg-[#2F7D5A] hover:bg-[#276848] text-white gap-1" data-testid="summary-approve-btn"><Check size={14} /> Approve</Button>
            ) : (
              <>
                <span className="inline-flex items-center gap-1 text-[13px] text-[#2F7D5A] font-medium"><Check size={14} /> Approved</span>
                <Button size="sm" onClick={openEmail} disabled={busy} className="bg-[#1C4358] hover:bg-[#163545] text-white gap-1" data-testid="summary-email-family-btn"><Mail size={14} /> Email Family Summary</Button>
              </>
            )}
          </div>

          {s.status === "approved" && lastEmail && (
            <div className="flex items-start gap-1.5 text-[12px] text-[#4B5563] rounded-lg bg-[#F1F5F8] px-3 py-2" data-testid="summary-last-emailed">
              <Mail size={13} className="mt-0.5 shrink-0 text-[#1C4358]" />
              <span>
                Last emailed: <b className="text-[#252221]">{fmtDT(lastEmail.at)}</b>
                {lastEmail.by_name ? <> by {lastEmail.by_name}</> : null} — to {lastEmail.to}
                {" · "}<span style={{ color: (EMAIL_STATUS[lastEmail.status] || {}).color || "#6B7280" }} className="font-medium" data-testid="summary-last-emailed-status">{(EMAIL_STATUS[lastEmail.status] || { label: lastEmail.status }).label}</span>
                {lastEmail.count > 1 ? <span className="text-[#9AA5B1]"> · sent {lastEmail.count}×</span> : null}
              </span>
            </div>
          )}

          {s.status === "approved" && (
            <div className="rounded-lg border border-[#2F7D5A]/30 bg-[#2F7D5A]/6 p-3" data-testid="summary-apply">
              <div className="text-[12px] font-semibold text-[#1C4358] mb-2 flex items-center gap-1"><CalendarPlus size={14} /> Apply to CRM & family (you choose what to apply)</div>
              <div className="space-y-1.5 text-[13px]">
                <label className="flex items-center gap-2"><Switch checked={apply.add_checklist} onCheckedChange={(v) => setApply({ ...apply, add_checklist: v })} data-testid="apply-checklist-toggle" /> Add checklist items to the meeting (client-visible ones show to the family)</label>
                <label className="flex items-center gap-2"><Switch checked={apply.create_tasks} onCheckedChange={(v) => setApply({ ...apply, create_tasks: v })} data-testid="apply-tasks-toggle" /> Create staff follow-up tasks</label>
                <label className="flex items-center gap-2"><Switch checked={apply.set_next_meeting} onCheckedChange={(v) => setApply({ ...apply, set_next_meeting: v })} data-testid="apply-nextmeeting-toggle" /> Save suggested next meeting date</label>
              </div>
              <Button size="sm" onClick={doApply} disabled={busy} className="mt-2 bg-[#1C4358] hover:bg-[#163545] text-white gap-1" data-testid="summary-apply-btn"><Send size={14} /> Apply now</Button>
            </div>
          )}
        </div>
      )}

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-lg" data-testid="summary-email-dialog" aria-describedby="summary-email-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1C4358]"><Mail size={18} /> Email family summary</DialogTitle>
            <DialogDescription id="summary-email-desc">Review exactly what the family will receive, then send.</DialogDescription>
          </DialogHeader>
          {previewLoading || !preview ? (
            <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#1C4358]" size={26} /></div>
          ) : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              {lastEmail && (
                <div className="flex items-start gap-2 rounded-lg bg-[#B7791F]/10 text-[#8a5d13] px-3 py-2 text-[12px]" data-testid="summary-email-resend-warning">
                  <ShieldAlert size={14} className="mt-0.5 shrink-0" /> This meeting summary was already emailed on <b>{fmtDT(lastEmail.at)}</b>{lastEmail.by_name ? ` by ${lastEmail.by_name}` : ""} to {lastEmail.to}. Do you want to send it again?
                </div>
              )}
              {!preview.provider_configured && (
                <div className="flex items-start gap-2 rounded-lg bg-[#B7791F]/10 text-[#8a5d13] px-3 py-2 text-[12px]" data-testid="summary-email-provider-warning">
                  <ShieldAlert size={14} className="mt-0.5 shrink-0" /> No email provider is configured yet. The message will be saved to the outbox and delivered once email is set up.
                </div>
              )}
              <div className="rounded-lg border border-[#E6EDF2] p-3 text-[13px] space-y-1.5">
                <div className="flex justify-between gap-2"><span className="text-[#6B7280]">To</span><span className="font-medium text-[#252221]" data-testid="summary-email-recipient">{preview.preview.recipient_name} · {preview.preview.recipient_masked}</span></div>
                <div className="flex justify-between gap-2"><span className="text-[#6B7280]">Subject</span><span className="font-medium text-[#252221] text-right" data-testid="summary-email-subject">{preview.preview.subject}</span></div>
                {preview.preview.meeting_date_pretty && <div className="flex justify-between gap-2"><span className="text-[#6B7280]">Meeting</span><span className="text-[#252221] text-right flex items-center gap-1"><CalendarClock size={13} /> {preview.preview.meeting_date_pretty}</span></div>}
              </div>
              {preview.preview.summary_text && (
                <div><div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-1">What we talked about</div>
                  <p className="text-[13px] text-[#252221] leading-relaxed">{preview.preview.summary_text}</p></div>
              )}
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-1 flex items-center gap-1"><ListChecks size={13} /> Checklist the family will see</div>
                {preview.preview.checklist.length === 0 ? <p className="text-[12px] text-[#9AA5B1]">No client-visible checklist items. (Internal items are never included.)</p> : (
                  <ul className="space-y-1" data-testid="summary-email-checklist">
                    {preview.preview.checklist.map((c, i) => <li key={i} className="flex items-start gap-2 text-[13px] text-[#252221]"><Check size={14} className="text-[#2F7D5A] mt-0.5 shrink-0" /><span>{c.title}</span></li>)}
                  </ul>
                )}
              </div>
              {preview.preview.documents_requested.length > 0 && (
                <div><div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-1 flex items-center gap-1"><FileText size={13} /> Documents requested</div>
                  <ul className="space-y-1" data-testid="summary-email-docs">
                    {preview.preview.documents_requested.map((n, i) => <li key={i} className="text-[13px] text-[#252221]">• {n}</li>)}
                  </ul></div>
              )}
              {preview.preview.next_meeting_date && (
                <div className="text-[13px] text-[#1C4358]"><b>Suggested next meeting:</b> {preview.preview.next_meeting_date}</div>
              )}
              {preview.preview.link && (
                <div className="text-[12px] text-[#1C4358] flex items-center gap-1"><ExternalLink size={13} /> Includes a link to their {preview.preview.link_kind === "guest" ? "secure room" : "family portal"}.</div>
              )}
              <div className="flex items-start gap-1.5 rounded-lg bg-[#2F7D5A]/8 text-[#276848] px-3 py-2 text-[12px]">
                <ShieldCheck size={14} className="mt-0.5 shrink-0" /> Internal notes, staff-only items, revenue and any legal/medical/financial/placement advice are never included.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)} disabled={sending} className="border-[#D6E0E7]" data-testid="summary-email-cancel-btn">Cancel</Button>
            <Button onClick={sendEmail} disabled={sending || previewLoading || !preview} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1.5" data-testid="summary-email-send-btn">
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} {lastEmail ? "Send Again" : "Send to family"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
